import { WiHorizon, WiDaySunny, WiMoonWaningCrescent3 } from 'react-icons/wi';

import { ObjectValues } from 'types/common';

import { sortOptimalDayValues } from '../utils/optimalDayUtils';

/**
 * Available optimal day values for challenge scheduling
 */
const OPTIMAL_DAY_VALUES = {
  MORNING: 'morning',
  DAY: 'day',
  EVENING: 'evening',
} as const;

/**
 * Labels for each optimal day value
 */
const OPTIMAL_DAY_LABELS = {
  [OPTIMAL_DAY_VALUES.MORNING]: 'Morning',
  [OPTIMAL_DAY_VALUES.DAY]: 'Afternoon',
  [OPTIMAL_DAY_VALUES.EVENING]: 'Evening',
} as const;

/**
 * Icon components for each optimal day value
 */
const OPTIMAL_DAY_ICONS = {
  [OPTIMAL_DAY_VALUES.MORNING]: WiHorizon,
  [OPTIMAL_DAY_VALUES.DAY]: WiDaySunny,
  [OPTIMAL_DAY_VALUES.EVENING]: WiMoonWaningCrescent3,
} as const;

/**
 * CSS variable names for optimal day background colors
 */
const OPTIMAL_DAY_CSS_VARS = {
  skip: 'var(--optimalDayBucketSkip)',
  anytime: 'var(--optimalDayBucketAnytime)',
  [OPTIMAL_DAY_VALUES.MORNING]: 'var(--optimalDayBucketMorning)',
  [OPTIMAL_DAY_VALUES.DAY]: 'var(--optimalDayBucketDay)',
  [OPTIMAL_DAY_VALUES.EVENING]: 'var(--optimalDayBucketEvening)',
} as const;

/**
 * Mapping between optimal day values and target completion time tags
 */
const TARGET_COMPLETION_TIME = {
  [OPTIMAL_DAY_VALUES.MORNING]: 'execution_morning',
  [OPTIMAL_DAY_VALUES.DAY]: 'execution_midday',
  [OPTIMAL_DAY_VALUES.EVENING]: 'execution_evening',
} as const;

export type OptimalDayValue = ObjectValues<typeof OPTIMAL_DAY_VALUES>;

export type DisplayBucketOrders =
  | Partial<Record<OptimalDayValue, number>>
  | { anytime?: number };

export {
  OPTIMAL_DAY_VALUES,
  OPTIMAL_DAY_LABELS,
  OPTIMAL_DAY_ICONS,
  TARGET_COMPLETION_TIME,
};

export interface OptimalDayColors {
  backgroundColor: string;
  textColor: string;
}

/**
 * Hook for optimal day functionality including colors, emojis, and display utilities
 */
export const useOptimalDay = () => {
  /**
   * Get styling colors for an optimal day value
   */
  const getOptimalDayColors = (
    optimalDayValues: OptimalDayValue[]
  ): OptimalDayColors => {
    if (optimalDayValues.length === 0) {
      return {
        backgroundColor: OPTIMAL_DAY_CSS_VARS.skip,
        textColor: 'inherit',
      };
    }

    if (optimalDayValues.length === Object.keys(OPTIMAL_DAY_VALUES).length) {
      return {
        backgroundColor: OPTIMAL_DAY_CSS_VARS.anytime,
        textColor: 'inherit',
      };
    }

    const colorMap: Record<OptimalDayValue, OptimalDayColors> = {
      morning: {
        backgroundColor: OPTIMAL_DAY_CSS_VARS.morning,
        textColor: 'inherit',
      },
      day: {
        backgroundColor: OPTIMAL_DAY_CSS_VARS.day,
        textColor: 'inherit',
      },
      evening: {
        backgroundColor: OPTIMAL_DAY_CSS_VARS.evening,
        textColor: 'inherit',
      },
    };

    return (
      colorMap[optimalDayValues[0]] || {
        backgroundColor: 'transparent',
        textColor: 'inherit',
      }
    );
  };

  /**
   * Get background color for an optimal day value
   */
  const getOptimalDayBackgroundColor = (
    optimalDayValues: OptimalDayValue[]
  ): string => {
    return getOptimalDayColors(optimalDayValues).backgroundColor;
  };

  /**
   * Get text color for an optimal day value
   */
  const getOptimalDayTextColor = (
    optimalDayValues: OptimalDayValue[]
  ): string => {
    return getOptimalDayColors(optimalDayValues).textColor;
  };

  /**
   * Get icon component for an optimal day value
   */
  const getOptimalDayIcon = (optimalDayValue: OptimalDayValue | null) => {
    if (!optimalDayValue) return null;
    return OPTIMAL_DAY_ICONS[optimalDayValue] || null;
  };

  return {
    getOptimalDayColors,
    getOptimalDayBackgroundColor,
    getOptimalDayTextColor,
    getOptimalDayIcon,
  };
};

/**
 * Convert DisplayBucketOrders to array of OptimalDayValues
 */
export const convertDisplayBucketOrdersToOptimalDayValue = (
  displayBucketOrders: DisplayBucketOrders = {}
): OptimalDayValue[] => {
  if ('anytime' in displayBucketOrders) {
    return [
      OPTIMAL_DAY_VALUES.MORNING,
      OPTIMAL_DAY_VALUES.DAY,
      OPTIMAL_DAY_VALUES.EVENING,
    ];
  }

  const values = Object.keys(displayBucketOrders).filter(
    (key): key is OptimalDayValue =>
      Object.values(OPTIMAL_DAY_VALUES).includes(key as OptimalDayValue)
  );

  return sortOptimalDayValues(values) as OptimalDayValue[];
};

/**
 * Convert the current default execution time tag to OptimalDayValue array
 */
export const convertTargetCompletionTimeTagToOptimalDayValue = (
  tags: (string | null)[]
): OptimalDayValue[] => {
  const executionTagToOptimalDay: Record<string, OptimalDayValue> = {
    [TARGET_COMPLETION_TIME[OPTIMAL_DAY_VALUES.MORNING]]:
      OPTIMAL_DAY_VALUES.MORNING,
    [TARGET_COMPLETION_TIME[OPTIMAL_DAY_VALUES.DAY]]: OPTIMAL_DAY_VALUES.DAY,
    [TARGET_COMPLETION_TIME[OPTIMAL_DAY_VALUES.EVENING]]:
      OPTIMAL_DAY_VALUES.EVENING,
  };

  const values = tags
    .filter(
      (tag): tag is string => tag !== null && tag.startsWith('execution_')
    )
    .map((tag) => executionTagToOptimalDay[tag])
    .filter((value): value is OptimalDayValue => value !== undefined);

  return sortOptimalDayValues(values) as OptimalDayValue[];
};
